class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  connectedCallback() {
    const facetForm = this.querySelector('form');
    if (facetForm && !facetForm._hbcBound) {
      facetForm._hbcBound = true;
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams =
        event.state && event.state.searchParams !== undefined
          ? event.state.searchParams
          : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.searchParamsPrev = searchParams;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    // Delegated change listener for desktop sort dropdown
    document.addEventListener('change', (e) => {
      const sortSelect = e.target.closest('#FacetSortForm select[name="sort_by"], .hbc-desktop-sort select[name="sort_by"]');
      if (sortSelect) {
        const facetFormElem = sortSelect.closest('facet-filters-form');
        if (facetFormElem && typeof facetFormElem.onSubmitHandler === 'function') {
          facetFormElem.onSubmitHandler(e);
        } else {
          FacetFiltersForm.prototype.onSubmitHandler.call(facetFormElem || document, e);
        }
      }
    });

    // Drawer open sync listener
    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('.mobile-facets__open-wrapper, .mobile-facets__open');
      if (openBtn) {
        FacetFiltersForm.syncCradleCardsFromURL();
        FacetFiltersForm.syncPriceCardsFromURL();
        FacetFiltersForm.syncAgeCardsFromURL();
        FacetFiltersForm.applyPriceAndCradleFilters();
      }
    });

    // Delegated click listener for dynamic client-side pagination links
    document.addEventListener('click', (e) => {
      const pageLink = e.target.closest('.hbc-pagination-link');
      if (pageLink) {
        e.preventDefault();
        const targetPage = parseInt(pageLink.dataset.page, 10);
        if (!isNaN(targetPage) && targetPage !== FacetFiltersForm.currentFilteredPage) {
          FacetFiltersForm.currentFilteredPage = targetPage;
          FacetFiltersForm.applyPriceAndCradleFilters(FacetFiltersForm.searchParamsPrev);
          const grid = document.getElementById('product-grid');
          if (grid) {
            grid.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    });

    // Delegated listener for cradle type checkboxes and radios (desktop & mobile)
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!target || (!target.classList.contains('hbc-cradle-card__checkbox') && !target.classList.contains('hbc-cradle-type-radio'))) return;

      const card = target.closest('.hbc-cradle-card');
      const isChecked = target.checked;
      const typeId = (target.dataset.typeId || target.value || '').toLowerCase().trim();
      const isMobile = Boolean(target.closest('#FacetFiltersFormMobile'));

      if (isMobile) {
        if (!typeId || typeId === 'all') {
          // If "All Types" is clicked in mobile drawer:
          card?.classList.toggle('is-checked', isChecked);
          if (isChecked) {
            // Uncheck all specific cradle options in mobile drawer
            document.querySelectorAll('#FacetFiltersFormMobile .hbc-cradle-card__checkbox, #FacetFiltersFormMobile .hbc-cradle-type-radio').forEach((box) => {
              const bId = (box.dataset.typeId || box.value || '').toLowerCase().trim();
              if (bId && bId !== 'all') {
                box.checked = false;
                box.closest('.hbc-cradle-card')?.classList.remove('is-checked');
              }
            });
          }
          return;
        }

        // Specific option clicked in mobile drawer:
        card?.classList.toggle('is-checked', isChecked);
        if (isChecked) {
          // Uncheck "All Types" in mobile drawer
          document.querySelectorAll('#FacetFiltersFormMobile .hbc-cradle-card__checkbox[data-type-id="all"], #FacetFiltersFormMobile .hbc-cradle-card__checkbox[value="all"], #FacetFiltersFormMobile .hbc-cradle-card__checkbox[data-type-id=""], #FacetFiltersFormMobile .hbc-cradle-card__checkbox[value=""]').forEach((box) => {
            box.checked = false;
            box.closest('.hbc-cradle-card')?.classList.remove('is-checked');
          });
        }
        return;
      }

      // On desktop:
      if (!typeId || typeId === 'all') {
        card?.classList.toggle('is-checked', isChecked);
        // Sync "All Types" to mobile drawer
        document.querySelectorAll('#FacetFiltersFormMobile .hbc-cradle-card__checkbox[data-type-id="all"], #FacetFiltersFormMobile .hbc-cradle-card__checkbox[value="all"], #FacetFiltersFormMobile .hbc-cradle-card__checkbox[data-type-id=""], #FacetFiltersFormMobile .hbc-cradle-card__checkbox[value=""]').forEach((mBox) => {
          mBox.checked = isChecked;
          mBox.closest('.hbc-cradle-card')?.classList.toggle('is-checked', isChecked);
        });

        if (isChecked) {
          // Uncheck all specific cradle options on desktop and mobile
          document.querySelectorAll('.hbc-cradle-card__checkbox, .hbc-cradle-type-radio').forEach((box) => {
            const bId = (box.dataset.typeId || box.value || '').toLowerCase().trim();
            if (bId && bId !== 'all') {
              box.checked = false;
              box.closest('.hbc-cradle-card')?.classList.remove('is-checked');
            }
          });
        }

        const params = new URLSearchParams(window.location.search);
        if (isChecked) {
          params.set('cradle_type', 'all');
        } else {
          params.delete('cradle_type');
        }
        params.delete('page');
        FacetFiltersForm.currentFilteredPage = 1;

        const searchStr = params.toString();
        FacetFiltersForm.searchParamsPrev = searchStr;
        FacetFiltersForm.updateURLHash(searchStr);
        FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
        return;
      }

      // Specific option clicked on desktop:
      card?.classList.toggle('is-checked', isChecked);
      // Sync this specific option to mobile drawer
      document.querySelectorAll(`#FacetFiltersFormMobile .hbc-cradle-card__checkbox[data-type-id="${typeId}"], #FacetFiltersFormMobile .hbc-cradle-card__checkbox[value="${typeId}"]`).forEach((mBox) => {
        mBox.checked = isChecked;
        mBox.closest('.hbc-cradle-card')?.classList.toggle('is-checked', isChecked);
      });

      if (isChecked) {
        // Uncheck "All Types" everywhere
        document.querySelectorAll('.hbc-cradle-card__checkbox[data-type-id="all"], .hbc-cradle-card__checkbox[value="all"], .hbc-cradle-card__checkbox[data-type-id=""], .hbc-cradle-card__checkbox[value=""]').forEach((box) => {
          box.checked = false;
          box.closest('.hbc-cradle-card')?.classList.remove('is-checked');
        });
      }

      // Collect all selected specific cradle types
      const checkedBoxes = document.querySelectorAll(
        '#FacetsWrapperDesktop .hbc-cradle-card__checkbox:checked, #FacetFiltersForm .hbc-cradle-card__checkbox:checked, #FacetsWrapperDesktop .hbc-cradle-type-radio:checked, #FacetFiltersForm .hbc-cradle-type-radio:checked'
      );
      const selectedTypes = Array.from(checkedBoxes)
        .map((b) => (b.dataset.typeId || b.value || '').toLowerCase().trim())
        .filter((t) => Boolean(t) && t !== 'all');

      const params = new URLSearchParams(window.location.search);
      if (selectedTypes.length > 0) {
        params.set('cradle_type', selectedTypes.join(','));
      } else {
        params.delete('cradle_type');
      }
      params.delete('page');
      FacetFiltersForm.currentFilteredPage = 1;

      const searchStr = params.toString();
      FacetFiltersForm.searchParamsPrev = searchStr;
      FacetFiltersForm.updateURLHash(searchStr);
      FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
    });

    // Delegated listener for price range card checkboxes (desktop & mobile)
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target || !target.classList.contains('hbc-price-card__checkbox')) return;

      const card = target.closest('.hbc-price-card');
      const isChecked = target.checked;
      const rangeId = target.dataset.rangeId;
      const isMobile = Boolean(target.closest('#FacetFiltersFormMobile'));

      if (isMobile) {
        // In mobile drawer: just toggle visual is-checked class on the card
        // Multi-select allowed: selecting one option NEVER unchecks another!
        // Selections are retained until user clicks "Apply filters"
        card?.classList.toggle('is-checked', isChecked);
        return;
      }

      // On desktop:
      card?.classList.toggle('is-checked', isChecked);

      // Sync checked state to mobile drawer checkbox for the same range
      if (rangeId) {
        document.querySelectorAll(`#FacetFiltersFormMobile .hbc-price-card__checkbox[data-range-id="${rangeId}"]`).forEach((mBox) => {
          mBox.checked = isChecked;
          mBox.closest('.hbc-price-card')?.classList.toggle('is-checked', isChecked);
        });
      }

      // Collect all currently checked desktop price range checkboxes
      const checkedBoxes = document.querySelectorAll(
        '#FacetsWrapperDesktop .hbc-price-card__checkbox:checked, #FacetFiltersForm .hbc-price-card__checkbox:checked'
      );
      const selectedRanges = Array.from(checkedBoxes)
        .map((b) => b.dataset.rangeId)
        .filter(Boolean);

      const params = new URLSearchParams(window.location.search);
      if (selectedRanges.length > 0) {
        params.set('price_range', selectedRanges.join(','));
      } else {
        params.delete('price_range');
      }
      params.delete('filter.v.price.gte');
      params.delete('filter.v.price.lte');
      params.delete('filter.v.availability');
      params.delete('page');
      FacetFiltersForm.currentFilteredPage = 1;

      const searchStr = params.toString();
      FacetFiltersForm.searchParamsPrev = searchStr;
      FacetFiltersForm.updateURLHash(searchStr);
      FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
    });

    // Delegated listener for age group card checkboxes (desktop & mobile)
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target || !target.classList.contains('hbc-age-card__checkbox')) return;

      const card = target.closest('.hbc-age-card');
      const isChecked = target.checked;
      const ageId = target.dataset.ageId || target.value;
      const isMobile = Boolean(target.closest('#FacetFiltersFormMobile'));

      if (isMobile) {
        card?.classList.toggle('is-checked', isChecked);
        return;
      }

      // On desktop: live toggle and sync
      card?.classList.toggle('is-checked', isChecked);

      // Sync checked state to mobile drawer checkbox for the same age group
      if (ageId) {
        document.querySelectorAll(`#FacetFiltersFormMobile .hbc-age-card__checkbox[data-age-id="${ageId}"]`).forEach((mBox) => {
          mBox.checked = isChecked;
          mBox.closest('.hbc-age-card')?.classList.toggle('is-checked', isChecked);
        });
      }

      // Collect all currently checked desktop age checkboxes
      const checkedBoxes = document.querySelectorAll(
        '#FacetsWrapperDesktop .hbc-age-card__checkbox:checked, #FacetFiltersForm .hbc-age-card__checkbox:checked'
      );
      const selectedAges = Array.from(checkedBoxes)
        .map((b) => b.dataset.ageId || b.value)
        .filter(Boolean);

      const params = new URLSearchParams(window.location.search);
      params.delete('filter.p.m.custom.age_group');
      if (selectedAges.length > 0) {
        params.set('age_group', selectedAges.join(','));
      } else {
        params.delete('age_group');
      }
      params.delete('page');
      FacetFiltersForm.currentFilteredPage = 1;

      const searchStr = params.toString();
      FacetFiltersForm.searchParamsPrev = searchStr;
      FacetFiltersForm.updateURLHash(searchStr);
      FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
    });

    // Delegated click listener for applied filter chips, clear-all, and mobile apply
    document.addEventListener('click', (event) => {
      // 1. Mobile Apply filters button
      const applyBtn = event.target.closest(
        '.mobile-facets__apply-button, #FacetsWrapperMobile .mobile-facets__footer .button, #FacetsWrapperMobile .mobile-facets__footer .button--primary'
      );
      if (applyBtn) {
        event.preventDefault();
        const mobileForm = document.getElementById('FacetFiltersFormMobile') || applyBtn.closest('form');
        if (mobileForm) {
          // Close the mobile drawer
          const details = applyBtn.closest('details') || document.querySelector('#FacetsWrapperMobile details') || document.querySelector('details.mobile-facets__disclosure');
          if (details) {
            details.removeAttribute('open');
            details.classList.remove('menu-opening');
            const summary = details.querySelector('summary');
            if (summary) summary.setAttribute('aria-expanded', 'false');
          }

          // Gather all checked price ranges in mobile drawer
          const mobilePriceBoxes = mobileForm.querySelectorAll('.hbc-price-card__checkbox:checked');
          const mobilePriceRanges = Array.from(mobilePriceBoxes)
            .map((b) => b.dataset.rangeId)
            .filter(Boolean);

          // Sync to desktop checkboxes
          document.querySelectorAll('#FacetsWrapperDesktop .hbc-price-card__checkbox, #FacetFiltersForm .hbc-price-card__checkbox').forEach((dBox) => {
            const matches = mobilePriceRanges.includes(dBox.dataset.rangeId);
            dBox.checked = matches;
            dBox.closest('.hbc-price-card')?.classList.toggle('is-checked', matches);
          });

          // Gather all checked age groups in mobile drawer
          const mobileAgeBoxes = mobileForm.querySelectorAll('.hbc-age-card__checkbox:checked');
          const mobileAges = Array.from(mobileAgeBoxes)
            .map((b) => b.dataset.ageId || b.value)
            .filter(Boolean);

          // Sync to desktop age checkboxes
          document.querySelectorAll('#FacetsWrapperDesktop .hbc-age-card__checkbox, #FacetFiltersForm .hbc-age-card__checkbox').forEach((dBox) => {
            const matches = mobileAges.includes(dBox.dataset.ageId || dBox.value);
            dBox.checked = matches;
            dBox.closest('.hbc-age-card')?.classList.toggle('is-checked', matches);
          });

          // Sync mobile cradle type to desktop
          const mobileCradleRadio = mobileForm.querySelector('.hbc-cradle-type-radio:checked');
          const mobileCradleVal = mobileCradleRadio ? (mobileCradleRadio.value || '').toLowerCase().trim() : '';
          document.querySelectorAll('#FacetsWrapperDesktop .hbc-cradle-type-radio, #FacetFiltersForm .hbc-cradle-type-radio').forEach((radio) => {
            const val = (radio.value || '').toLowerCase().trim();
            radio.checked = !mobileCradleVal || mobileCradleVal === 'all' ? (val === '' || val === 'all') : val === mobileCradleVal;
          });

          // Build search params
          const formElement = mobileForm.tagName.toLowerCase() === 'form' ? mobileForm : mobileForm.querySelector('form');
          const formData = new FormData(formElement || mobileForm);
          const params = new URLSearchParams(formData);

          const mobileCradleBoxes = mobileForm.querySelectorAll('.hbc-cradle-card__checkbox:checked, .hbc-cradle-type-radio:checked');
          const mobileCradleTypes = Array.from(mobileCradleBoxes)
            .map((b) => (b.dataset.typeId || b.value || '').toLowerCase().trim())
            .filter((t) => Boolean(t) && t !== 'all');

          if (mobileCradleTypes.length > 0) {
            params.set('cradle_type', mobileCradleTypes.join(','));
          } else {
            params.delete('cradle_type');
          }

          if (mobilePriceRanges.length > 0) {
            params.set('price_range', mobilePriceRanges.join(','));
          } else {
            params.delete('price_range');
          }

          params.delete('filter.p.m.custom.age_group');
          if (mobileAges.length > 0) {
            params.set('age_group', mobileAges.join(','));
          } else {
            params.delete('age_group');
          }
          params.delete('filter.v.price.gte');
          params.delete('filter.v.price.lte');
          params.delete('filter.v.availability');
          params.delete('page');
          FacetFiltersForm.currentFilteredPage = 1;

          const searchStr = params.toString();
          FacetFiltersForm.searchParamsPrev = searchStr;
          FacetFiltersForm.updateURLHash(searchStr);
          FacetFiltersForm.renderPage(searchStr, event);
          FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
        }
        return;
      }

      // 2. Clear all / Reset all (Desktop & Mobile)
      const clearBtn = event.target.closest(
        '.hbc-applied-filters__clear, .active-facets__button-remove, .hbc-empty-reset-btn, .hbc-empty-state__reset, #FacetsWrapperMobile .mobile-facets__clear, .mobile-facets__clear-wrapper a'
      );
      if (clearBtn) {
        event.preventDefault();
        const details = clearBtn.closest('details');
        if (details) {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
          const summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'false');
        }
        FacetFiltersForm.clearAllFilters();
        return;
      }

      // 3. Cradle Type applied filter chip
      const cradleChip = event.target.closest('.hbc-applied-filters__chip--cradle-type');
      if (cradleChip) {
        event.preventDefault();
        const typeToRemove = (cradleChip.dataset.typeId || '').toLowerCase().trim();
        const currentParams = new URLSearchParams(window.location.search);
        let selectedTypes = FacetFiltersForm.getSelectedCradleTypes(currentParams.toString());
        if (typeToRemove === 'all') {
          selectedTypes = [];
          currentParams.delete('cradle_type');
          document.querySelectorAll('.hbc-cradle-card__checkbox[data-type-id="all"], .hbc-cradle-card__checkbox[value="all"], .hbc-cradle-card__checkbox[data-type-id=""], .hbc-cradle-card__checkbox[value=""]').forEach((box) => {
            box.checked = false;
            box.closest('.hbc-cradle-card')?.classList.remove('is-checked');
          });
        } else if (typeToRemove) {
          selectedTypes = selectedTypes.filter((t) => t !== typeToRemove);
          document.querySelectorAll(`.hbc-cradle-card__checkbox[data-type-id="${typeToRemove}"]`).forEach((box) => {
            box.checked = false;
            box.closest('.hbc-cradle-card')?.classList.remove('is-checked');
          });
        }

        if (selectedTypes.length > 0) {
          currentParams.set('cradle_type', selectedTypes.join(','));
        } else {
          currentParams.delete('cradle_type');
        }
        currentParams.delete('page');
        FacetFiltersForm.currentFilteredPage = 1;

        const searchStr = currentParams.toString();
        FacetFiltersForm.searchParamsPrev = searchStr;
        FacetFiltersForm.updateURLHash(searchStr);
        FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
        return;
      }

      // 4. Price range applied filter chip
      const priceChip = event.target.closest('.hbc-applied-filters__chip--price');
      if (priceChip) {
        event.preventDefault();
        const rangeIdToRemove = priceChip.dataset.rangeId;
        const currentParams = new URLSearchParams(window.location.search);
        let selectedRanges = FacetFiltersForm.getSelectedPriceRanges(currentParams.toString());
        selectedRanges = selectedRanges.filter((r) => r !== rangeIdToRemove);

        // Uncheck this checkbox everywhere
        document.querySelectorAll(`.hbc-price-card__checkbox[data-range-id="${rangeIdToRemove}"]`).forEach((box) => {
          box.checked = false;
          box.closest('.hbc-price-card')?.classList.remove('is-checked');
        });

        if (selectedRanges.length > 0) {
          currentParams.set('price_range', selectedRanges.join(','));
        } else {
          currentParams.delete('price_range');
        }
        currentParams.delete('filter.v.price.gte');
        currentParams.delete('filter.v.price.lte');
        currentParams.delete('filter.v.availability');
        currentParams.delete('page');
        FacetFiltersForm.currentFilteredPage = 1;

        const searchStr = currentParams.toString();
        FacetFiltersForm.searchParamsPrev = searchStr;
        FacetFiltersForm.updateURLHash(searchStr);
        FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
        return;
      }

      // 4b. Age group applied filter chip
      const ageChip = event.target.closest('.hbc-applied-filters__chip--age');
      if (ageChip) {
        event.preventDefault();
        const ageIdToRemove = ageChip.dataset.ageId;
        const currentParams = new URLSearchParams(window.location.search);
        let selectedAges = FacetFiltersForm.getSelectedAgeGroups(currentParams.toString());
        selectedAges = selectedAges.filter((a) => a !== ageIdToRemove);

        // Uncheck this checkbox everywhere
        document.querySelectorAll(`.hbc-age-card__checkbox[data-age-id="${ageIdToRemove}"]`).forEach((box) => {
          box.checked = false;
          box.closest('.hbc-age-card')?.classList.remove('is-checked');
        });

        currentParams.delete('filter.p.m.custom.age_group');
        if (selectedAges.length > 0) {
          currentParams.set('age_group', selectedAges.join(','));
        } else {
          currentParams.delete('age_group');
        }
        currentParams.delete('page');
        FacetFiltersForm.currentFilteredPage = 1;

        const searchStr = currentParams.toString();
        FacetFiltersForm.searchParamsPrev = searchStr;
        FacetFiltersForm.updateURLHash(searchStr);
        FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
        return;
      }

      // 5. Other native filter chips
      const chip = event.target.closest('.hbc-applied-filters__chip, .active-facets__button');
      if (
        chip &&
        chip.href &&
        !chip.classList.contains('hbc-applied-filters__chip--cradle-type') &&
        !chip.classList.contains('hbc-applied-filters__chip--price') &&
        !chip.classList.contains('hbc-applied-filters__chip--age') &&
        !chip.classList.contains('hbc-applied-filters__clear')
      ) {
        event.preventDefault();
        const url = chip.href.indexOf('?') === -1 ? '' : chip.href.slice(chip.href.indexOf('?') + 1);
        const urlParams = new URLSearchParams(url);
        const currentParams = new URLSearchParams(window.location.search);
        const currentCradleType = currentParams.get('cradle_type');
        if (currentCradleType && currentCradleType !== 'all') {
          urlParams.set('cradle_type', currentCradleType);
        }
        const currentPriceRange = currentParams.get('price_range');
        if (currentPriceRange) {
          urlParams.set('price_range', currentPriceRange);
        }
        const currentAgeGroup = currentParams.get('filter.p.m.custom.age_group') || currentParams.get('age_group');
        urlParams.delete('filter.p.m.custom.age_group');
        if (currentAgeGroup) {
          urlParams.set('age_group', currentAgeGroup);
        }
        urlParams.delete('filter.v.price.gte');
        urlParams.delete('filter.v.price.lte');
        FacetFiltersForm.renderPage(urlParams.toString());
      }
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    // Strip unconfigured native parameters before section fetch so Shopify server does not return 0 products
    const cleanFetchParams = new URLSearchParams(searchParams);
    cleanFetchParams.delete('filter.p.m.custom.age_group');
    cleanFetchParams.delete('filter.v.price.gte');
    cleanFetchParams.delete('filter.v.price.lte');
    cleanFetchParams.delete('filter.v.availability');
    const fetchQuery = cleanFetchParams.toString();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${fetchQuery}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        FacetFiltersForm.syncCradleCardsFromURL(FacetFiltersForm.searchParamsPrev);
        FacetFiltersForm.syncPriceCardsFromURL(FacetFiltersForm.searchParamsPrev);
        FacetFiltersForm.syncAgeCardsFromURL(FacetFiltersForm.searchParamsPrev);
        FacetFiltersForm.applyPriceAndCradleFilters(FacetFiltersForm.searchParamsPrev);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    FacetFiltersForm.syncCradleCardsFromURL(FacetFiltersForm.searchParamsPrev);
    FacetFiltersForm.syncPriceCardsFromURL(FacetFiltersForm.searchParamsPrev);
    FacetFiltersForm.syncAgeCardsFromURL(FacetFiltersForm.searchParamsPrev);
    FacetFiltersForm.applyPriceAndCradleFilters(FacetFiltersForm.searchParamsPrev);
  }

  static renderProductGridContainer(html) {
    const parsedContainer = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer');

    if (parsedContainer) {
      const serverPagination = parsedContainer.querySelector('.pagination-wrapper');
      FacetFiltersForm.originalPaginationHTML = serverPagination ? serverPagination.outerHTML : '';
      FacetFiltersForm.currentFilteredPage = 1;

      document.getElementById('ProductGridContainer').innerHTML = parsedContainer.innerHTML;
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    const checkedCradleBoxes = form.querySelectorAll('.hbc-cradle-card__checkbox:checked, .hbc-cradle-type-radio:checked');
    if (checkedCradleBoxes.length > 0) {
      const types = Array.from(checkedCradleBoxes)
        .map((b) => (b.dataset.typeId || b.value || '').toLowerCase().trim())
        .filter((t) => Boolean(t) && t !== 'all');
      if (types.length > 0) {
        params.set('cradle_type', types.join(','));
      } else {
        params.delete('cradle_type');
      }
    } else if (!form.querySelector('.hbc-cradle-card__checkbox, .hbc-cradle-type-radio')) {
      const currentCradle = new URLSearchParams(window.location.search).get('cradle_type');
      if (currentCradle && currentCradle !== 'all') {
        params.set('cradle_type', currentCradle);
      }
    } else {
      params.delete('cradle_type');
    }
    // Delete native min/max price bounds so Shopify does not filter price with conflicting min/max
    params.delete('filter.v.price.gte');
    params.delete('filter.v.price.lte');
    params.delete('filter.v.availability');

    const checkedBoxes = form.querySelectorAll('.hbc-price-card__checkbox:checked');
    if (checkedBoxes.length > 0) {
      const ranges = Array.from(checkedBoxes)
        .map((b) => b.dataset.rangeId)
        .filter(Boolean);
      params.set('price_range', ranges.join(','));
    } else if (!form.querySelector('.hbc-price-card__checkbox')) {
      // If form doesn't contain price checkboxes (e.g. FacetSortForm), preserve price_range
      const currentPriceRange = new URLSearchParams(window.location.search).get('price_range');
      if (currentPriceRange) params.set('price_range', currentPriceRange);
    } else {
      params.delete('price_range');
    }

    const checkedAgeBoxes = form.querySelectorAll('.hbc-age-card__checkbox:checked');
    params.delete('filter.p.m.custom.age_group');
    if (checkedAgeBoxes.length > 0) {
      const ages = Array.from(checkedAgeBoxes)
        .map((b) => b.dataset.ageId || b.value)
        .filter(Boolean);
      params.set('age_group', ages.join(','));
    } else if (!form.querySelector('.hbc-age-card__checkbox')) {
      const currentAgeGroup = new URLSearchParams(window.location.search).get('filter.p.m.custom.age_group') || new URLSearchParams(window.location.search).get('age_group');
      if (currentAgeGroup) {
        params.set('age_group', currentAgeGroup);
      }
    } else {
      params.delete('age_group');
    }
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    let searchParams = '';
    const isMobile = event.target.closest('form') && event.target.closest('form').id === 'FacetFiltersFormMobile';

    if (event.srcElement && event.srcElement.className == 'mobile-facets__checkbox') {
      searchParams = this.createSearchParams(event.target.closest('form'));
    } else {
      const forms = [];
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      const combinedParams = new URLSearchParams();
      forms.forEach((formStr) => {
        const p = new URLSearchParams(formStr);
        p.forEach((val, key) => combinedParams.set(key, val));
      });
      if (!isMobile) {
        const sortSelect = document.querySelector('#FacetSortForm select[name="sort_by"], .hbc-desktop-sort select[name="sort_by"]');
        if (sortSelect && sortSelect.value) {
          combinedParams.set('sort_by', sortSelect.value);
        }
      }
      searchParams = combinedParams.toString();
    }

    // Preserve active cradle_type if set
    const urlParams = new URLSearchParams(searchParams);
    const checkedCradles = isMobile
      ? document.querySelectorAll('#FacetFiltersFormMobile .hbc-cradle-card__checkbox:checked, #FacetFiltersFormMobile .hbc-cradle-type-radio:checked')
      : document.querySelectorAll('#FacetsWrapperDesktop .hbc-cradle-card__checkbox:checked, #FacetFiltersForm .hbc-cradle-card__checkbox:checked, .hbc-cradle-type-radio:checked');
    const isAllCheckedInForm = Array.from(checkedCradles).some((b) => {
      const id = (b.dataset.typeId || b.value || '').toLowerCase().trim();
      return !id || id === 'all';
    });
    const activeCradleTypes = Array.from(checkedCradles)
      .map((b) => (b.dataset.typeId || b.value || '').toLowerCase().trim())
      .filter((t) => Boolean(t) && t !== 'all');

    if (activeCradleTypes.length > 0) {
      urlParams.set('cradle_type', activeCradleTypes.join(','));
    } else if (isAllCheckedInForm) {
      urlParams.set('cradle_type', 'all');
    } else {
      urlParams.delete('cradle_type');
    }

    // Preserve active price_range if set
    const checkedPriceBoxes = isMobile
      ? document.querySelectorAll('#FacetFiltersFormMobile .hbc-price-card__checkbox:checked')
      : document.querySelectorAll('#FacetsWrapperDesktop .hbc-price-card__checkbox:checked, #FacetFiltersForm .hbc-price-card__checkbox:checked');
    const activePriceRanges = Array.from(checkedPriceBoxes)
      .map((b) => b.dataset.rangeId)
      .filter(Boolean);

    if (activePriceRanges.length > 0) {
      urlParams.set('price_range', activePriceRanges.join(','));
    } else {
      urlParams.delete('price_range');
    }

    // Preserve active age groups if set
    const checkedAgeBoxes = isMobile
      ? document.querySelectorAll('#FacetFiltersFormMobile .hbc-age-card__checkbox:checked')
      : document.querySelectorAll('#FacetsWrapperDesktop .hbc-age-card__checkbox:checked, #FacetFiltersForm .hbc-age-card__checkbox:checked');
    const activeAgeGroups = Array.from(checkedAgeBoxes)
      .map((b) => b.dataset.ageId || b.value)
      .filter(Boolean);

    urlParams.delete('filter.p.m.custom.age_group');
    if (activeAgeGroups.length > 0) {
      urlParams.set('age_group', activeAgeGroups.join(','));
    } else {
      urlParams.delete('age_group');
    }

    urlParams.delete('filter.v.price.gte');
    urlParams.delete('filter.v.price.lte');
    urlParams.delete('filter.v.availability');

    this.onSubmitForm(urlParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const urlParams = new URLSearchParams(url);
    const currentParams = new URLSearchParams(window.location.search);
    const currentCradleType = currentParams.get('cradle_type');
    if (currentCradleType && currentCradleType !== 'all') {
      urlParams.set('cradle_type', currentCradleType);
    }
    const currentAge = currentParams.get('filter.p.m.custom.age_group') || currentParams.get('age_group');
    urlParams.delete('filter.p.m.custom.age_group');
    if (currentAge) {
      urlParams.set('age_group', currentAge);
    }
    FacetFiltersForm.renderPage(urlParams.toString());
  }

  static matchesPriceRange(price, rangeId) {
    if (isNaN(price)) return true;
    switch (rangeId) {
      case 'under-1000':
        return price < 1000;
      case '1000-5000':
        return price >= 1000 && price <= 5000;
      case '5000-10000':
        return price >= 5000 && price <= 10000;
      case 'above-10000':
        return price > 10000;
      default:
        return true;
    }
  }

  static getSelectedPriceRanges(searchParams) {
    const params = new URLSearchParams(searchParams !== undefined ? searchParams : window.location.search);
    const rangeParam = params.get('price_range');
    if (rangeParam) {
      return rangeParam.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    }
    const gteStr = params.get('filter.v.price.gte');
    const lteStr = params.get('filter.v.price.lte');
    const gte = parseFloat(gteStr);
    const lte = parseFloat(lteStr);
    const hasGte = gteStr !== null && !isNaN(gte);
    const hasLte = lteStr !== null && !isNaN(lte);

    if (!hasGte && hasLte && lte <= 1000) {
      return ['under-1000'];
    }
    if (hasGte && gte >= 1000 && hasLte && lte <= 5000) {
      return ['1000-5000'];
    }
    if (hasGte && gte >= 5000 && hasLte && lte <= 10000) {
      return ['5000-10000'];
    }
    if (hasGte && gte >= 10000 && !hasLte) {
      return ['above-10000'];
    }
    return [];
  }

  static getPriceRangeLabel(rangeId) {
    const cards = document.querySelector('.hbc-price-cards');
    const curr = (cards && cards.dataset.currencySymbol) || '₹';
    switch (rangeId) {
      case 'under-1000':
        return `Under ${curr}1,000`;
      case '1000-5000':
        return `${curr}1,000–${curr}5,000`;
      case '5000-10000':
        return `${curr}5,000–${curr}10,000`;
      case 'above-10000':
        return `Above ${curr}10,000`;
      default:
        return rangeId;
    }
  }

  static syncPriceCardsFromURL(searchParams) {
    const selectedRanges = FacetFiltersForm.getSelectedPriceRanges(searchParams);
    const rangeSet = new Set(selectedRanges);

    document.querySelectorAll('.hbc-price-card__checkbox').forEach((checkbox) => {
      const id = checkbox.dataset.rangeId;
      const matches = rangeSet.has(id);
      checkbox.checked = matches;
      checkbox.closest('.hbc-price-card')?.classList.toggle('is-checked', matches);
    });

    document.querySelectorAll('.hbc-price-gte, .hbc-price-lte').forEach((input) => {
      input.value = '';
      input.setAttribute('disabled', 'disabled');
    });
  }

  static getSelectedAgeGroups(searchParams) {
    const params = new URLSearchParams(searchParams !== undefined ? searchParams : window.location.search);
    const ageParam = params.get('filter.p.m.custom.age_group') || params.get('age_group') || params.get('age');
    if (ageParam) {
      return ageParam.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    }
    const allMatches = [];
    params.forEach((value, key) => {
      if (key.includes('custom.age_group') || key === 'age_group') {
        value.split(',').forEach((val) => {
          const v = val.trim().toLowerCase();
          if (v && !allMatches.includes(v)) allMatches.push(v);
        });
      }
    });
    return allMatches;
  }

  static getAgeGroupLabel(ageId) {
    switch (ageId) {
      case '0-1':
        return 'Age 0–1 Year';
      case '1-2':
        return 'Age 1–2 Years';
      case '2-3':
        return 'Age 2–3 Years';
      case '3-4':
        return 'Age 3–4 Years';
      case '4-5':
        return 'Age 4–5 Years';
      default:
        return `Age ${ageId}`;
    }
  }

  static syncAgeCardsFromURL(searchParams) {
    const selectedAges = FacetFiltersForm.getSelectedAgeGroups(searchParams);
    const ageSet = new Set(selectedAges);

    document.querySelectorAll('.hbc-age-card__checkbox').forEach((checkbox) => {
      const id = checkbox.dataset.ageId || checkbox.value;
      const matches = ageSet.has(id);
      checkbox.checked = matches;
      checkbox.closest('.hbc-age-card')?.classList.toggle('is-checked', matches);
    });
  }

  static getSelectedCradleTypes(searchParams) {
    const params = new URLSearchParams(searchParams !== undefined ? searchParams : window.location.search);
    const cradleParam = (params.get('cradle_type') || '').toLowerCase().trim();
    if (!cradleParam || cradleParam === 'all') return [];
    return cradleParam.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  }

  static syncCradleCardsFromURL(searchParams) {
    const selectedTypes = FacetFiltersForm.getSelectedCradleTypes(searchParams);
    const typeSet = new Set(selectedTypes);
    const params = new URLSearchParams(searchParams !== undefined ? searchParams : window.location.search);
    const cradleParam = (params.get('cradle_type') || '').toLowerCase().trim();
    const isExplicitAll = cradleParam === 'all';

    document.querySelectorAll('.hbc-cradle-card__checkbox, .hbc-cradle-type-radio').forEach((box) => {
      const id = (box.dataset.typeId || box.value || '').toLowerCase().trim();
      let matches = false;
      if (!id || id === 'all') {
        matches = isExplicitAll;
      } else {
        matches = typeSet.has(id);
      }
      box.checked = matches;
      box.closest('.hbc-cradle-card')?.classList.toggle('is-checked', matches);
    });
  }

  static applyCradleTypeFilter(customParams) {
    FacetFiltersForm.applyPriceAndCradleFilters(customParams);
  }

  static applyPriceAndCradleFilters(customParams) {
    const params = new URLSearchParams(customParams !== undefined ? customParams : window.location.search);
    const selectedCradleTypes = FacetFiltersForm.getSelectedCradleTypes(params.toString());
    const isFilteringType = selectedCradleTypes.length > 0;

    // Sync cradle type inputs (desktop & mobile)
    FacetFiltersForm.syncCradleCardsFromURL(params.toString());

    const selectedPriceRanges = FacetFiltersForm.getSelectedPriceRanges(params.toString());
    const isFilteringPrice = selectedPriceRanges.length > 0;

    // Sync price checkboxes across desktop & mobile
    FacetFiltersForm.syncPriceCardsFromURL(params.toString());

    const selectedAgeGroups = FacetFiltersForm.getSelectedAgeGroups(params.toString());
    const isFilteringAge = selectedAgeGroups.length > 0;

    // Sync age checkboxes across desktop & mobile
    FacetFiltersForm.syncAgeCardsFromURL(params.toString());

    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;

    const items = Array.from(productGrid.querySelectorAll('li.grid__item'));
    const matchingItems = [];

    items.forEach((item) => {
      const itemType = (item.dataset.cradleType || '').toLowerCase().trim();
      const rawPrice = item.dataset.price;
      const itemPrice = rawPrice !== undefined && rawPrice !== '' ? parseFloat(rawPrice) : NaN;
      const rawAgeGroups = (item.dataset.ageGroup || '').toLowerCase().trim();
      const itemAgeGroups = rawAgeGroups ? rawAgeGroups.split(/[\s,]+/).filter(Boolean) : [];

      const matchesType = !isFilteringType || selectedCradleTypes.includes(itemType);
      const matchesPrice =
        !isFilteringPrice ||
        selectedPriceRanges.some((rangeId) => FacetFiltersForm.matchesPriceRange(itemPrice, rangeId));
      const matchesAge =
        !isFilteringAge ||
        selectedAgeGroups.some((ageId) => itemAgeGroups.includes(ageId));

      if (matchesType && matchesPrice && matchesAge) {
        matchingItems.push(item);
      }
    });

    const isFiltered = isFilteringType || isFilteringPrice || isFilteringAge;
    FacetFiltersForm.renderFilteredPagination(items, matchingItems, isFiltered);

    const visibleCount = matchingItems.length;

    // Update count labels when filtering by cradle type, price, or age
    if (isFiltered) {
      document.querySelectorAll('#ProductCount, #ProductCountDesktop, .hbc-facets__result-count').forEach((el) => {
        el.textContent = `Showing ${visibleCount} items`;
      });
    }

    // Toggle client-side empty state if filters eliminate all products
    let emptyState = document.querySelector('.hbc-empty-state--client');
    if (visibleCount === 0 && items.length > 0 && isFiltered) {
      if (!emptyState) {
        emptyState = document.createElement('div');
        emptyState.className = 'hbc-empty-state hbc-empty-state--client';
        emptyState.style.display = 'flex';
        emptyState.style.flexDirection = 'column';
        emptyState.style.alignItems = 'center';
        emptyState.style.padding = '4rem 2rem';
        emptyState.style.width = '100%';
        emptyState.innerHTML = `
          <h2 class="hbc-empty-state__title" style="font-size: 2rem; margin-bottom: 1rem;">No products match your filter criteria</h2>
          <p class="hbc-empty-state__text" style="font-size: 1.4rem; color: rgba(var(--color-foreground), 0.75); margin-bottom: 2rem;">Try choosing other filter options or clearing filters.</p>
          <button type="button" class="button button--primary hbc-empty-state__reset hbc-empty-reset-btn">Reset All Filters</button>
        `;
        productGrid.parentNode.insertBefore(emptyState, productGrid.nextSibling);
      } else {
        emptyState.style.display = 'flex';
      }
    } else if (emptyState) {
      emptyState.remove();
    }

    // Update applied filter chips for Cradle Type, Price Ranges, and Age Groups
    FacetFiltersForm.updateAppliedFilterChips(selectedCradleTypes, isFilteringType, selectedPriceRanges, selectedAgeGroups);
  }

  static renderFilteredPagination(allItems, matchingItems, isFiltered) {
    const productGrid = document.getElementById('product-grid');
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGrid || !productGridContainer) return;

    const perPage = parseInt(productGrid.dataset.productsPerPage, 10) || 12;

    if (!isFiltered) {
      allItems.forEach((item) => {
        item.style.display = '';
        item.classList.remove('hbc-item--hidden');
      });

      productGridContainer.querySelectorAll('.hbc-dynamic-pagination').forEach((el) => el.remove());

      const currentPagination = productGridContainer.querySelector('.pagination-wrapper');
      if (!currentPagination && FacetFiltersForm.originalPaginationHTML) {
        const temp = document.createElement('div');
        temp.innerHTML = FacetFiltersForm.originalPaginationHTML;
        const paginationEl = temp.firstElementChild;
        if (paginationEl) {
          const containerToAppend = productGridContainer.querySelector('.collection') || productGridContainer;
          containerToAppend.appendChild(paginationEl);
        }
      }
      return;
    }

    const totalMatching = matchingItems.length;
    const totalPages = Math.ceil(totalMatching / perPage);

    // If total matching is less than or equal to configured limit (e.g. 3 <= 12), display all without pagination
    if (totalPages <= 1) {
      matchingItems.forEach((item) => {
        item.style.display = '';
        item.classList.remove('hbc-item--hidden');
      });

      allItems.forEach((item) => {
        if (!matchingItems.includes(item)) {
          item.style.display = 'none';
          item.classList.add('hbc-item--hidden');
        }
      });

      productGridContainer.querySelectorAll('.pagination-wrapper').forEach((el) => el.remove());
      return;
    }

    // Matching items exceed limit (e.g. > 12): paginate matching items
    if (FacetFiltersForm.currentFilteredPage > totalPages) {
      FacetFiltersForm.currentFilteredPage = 1;
    }
    const currentPage = FacetFiltersForm.currentFilteredPage || 1;
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;

    matchingItems.forEach((item, index) => {
      if (index >= startIndex && index < endIndex) {
        item.style.display = '';
        item.classList.remove('hbc-item--hidden');
      } else {
        item.style.display = 'none';
        item.classList.add('hbc-item--hidden');
      }
    });

    allItems.forEach((item) => {
      if (!matchingItems.includes(item)) {
        item.style.display = 'none';
        item.classList.add('hbc-item--hidden');
      }
    });

    productGridContainer.querySelectorAll('.pagination-wrapper').forEach((el) => el.remove());

    let pageItemsHTML = '';
    for (let p = 1; p <= totalPages; p++) {
      if (p === currentPage) {
        pageItemsHTML += `
          <li>
            <a role="link" aria-disabled="true" class="pagination__item pagination__item--current" aria-current="page" aria-label="Page ${p}">
              ${p}
            </a>
          </li>
        `;
      } else {
        pageItemsHTML += `
          <li>
            <a href="#" class="pagination__item link hbc-pagination-link" data-page="${p}" aria-label="Page ${p}">
              ${p}
            </a>
          </li>
        `;
      }
    }

    const prevButtonHTML =
      currentPage > 1
        ? `<li>
            <a href="#" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce hbc-pagination-link" data-page="${currentPage - 1}" aria-label="Previous page">
              <span class="svg-wrapper">
                <svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>
              </span>
            </a>
          </li>`
        : `<li>
            <span class="pagination__item pagination__item--next pagination__item-arrow pagination__item-disabled">
              <span class="svg-wrapper">
                <svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>
              </span>
            </span>
          </li>`;

    const nextButtonHTML =
      currentPage < totalPages
        ? `<li>
            <a href="#" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce hbc-pagination-link" data-page="${currentPage + 1}" aria-label="Next page">
              <span class="svg-wrapper">
                <svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>
              </span>
            </a>
          </li>`
        : `<li>
            <span class="pagination__item pagination__item--prev pagination__item-arrow pagination__item-disabled">
              <span class="svg-wrapper">
                <svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>
              </span>
            </span>
          </li>`;

    const paginationWrapper = document.createElement('div');
    paginationWrapper.className = 'pagination-wrapper hbc-dynamic-pagination';
    paginationWrapper.dataset.page = String(currentPage);
    paginationWrapper.innerHTML = `
      <nav class="pagination" role="navigation" aria-label="Pagination">
        <ul class="pagination__list list-unstyled" role="list">
          ${prevButtonHTML}
          ${pageItemsHTML}
          ${nextButtonHTML}
        </ul>
      </nav>
    `;

    const containerToAppend = productGridContainer.querySelector('.collection') || productGridContainer;
    containerToAppend.appendChild(paginationWrapper);
  }

  static clearAllFilters() {
    FacetFiltersForm.currentFilteredPage = 1;
    // Uncheck and clear all cradle cards
    document.querySelectorAll('.hbc-cradle-card__checkbox, .hbc-cradle-type-radio').forEach((box) => {
      box.checked = false;
      box.closest('.hbc-cradle-card')?.classList.remove('is-checked');
    });

    // Uncheck and clear all price cards
    document.querySelectorAll('.hbc-price-card__checkbox').forEach((box) => {
      box.checked = false;
      box.closest('.hbc-price-card')?.classList.remove('is-checked');
    });
    document.querySelectorAll('.hbc-price-gte, .hbc-price-lte').forEach((input) => {
      input.value = '';
      input.setAttribute('disabled', 'disabled');
    });

    // Uncheck and clear all age cards
    document.querySelectorAll('.hbc-age-card__checkbox').forEach((box) => {
      box.checked = false;
      box.closest('.hbc-age-card')?.classList.remove('is-checked');
    });

    // Uncheck all filter checkboxes across desktop & mobile
    document.querySelectorAll('.mobile-facets__checkbox, .facet-checkbox input[type="checkbox"]').forEach((box) => {
      box.checked = false;
    });

    // Determine clean search params (preserve q and sort_by if present)
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();
    const q = currentParams.get('q');
    const sort = currentParams.get('sort_by');
    if (q) newParams.set('q', q);
    if (sort) newParams.set('sort_by', sort);

    const searchStr = newParams.toString();
    FacetFiltersForm.searchParamsPrev = searchStr;
    FacetFiltersForm.updateURLHash(searchStr);
    FacetFiltersForm.renderPage(searchStr);
    FacetFiltersForm.applyPriceAndCradleFilters(searchStr);
  }

  static updateAppliedFilterChips(selectedCradleTypes = [], isFilteringType = false, selectedPriceRanges = [], selectedAgeGroups = []) {
    let appliedContainer = document.querySelector('.hbc-applied-filters');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const params = new URLSearchParams(window.location.search);
    const cradleParam = (params.get('cradle_type') || '').toLowerCase().trim();
    const isExplicitAll = cradleParam === 'all';
    const effectiveCradleTypes = isExplicitAll ? ['all'] : selectedCradleTypes;
    const hasAnyActiveCustomFilter = isFilteringType || isExplicitAll || (selectedPriceRanges && selectedPriceRanges.length > 0) || (selectedAgeGroups && selectedAgeGroups.length > 0);

    if (hasAnyActiveCustomFilter && !appliedContainer && productGridContainer) {
      appliedContainer = document.createElement('div');
      appliedContainer.className = 'hbc-applied-filters small-hide';
      appliedContainer.setAttribute('aria-label', 'Applied filters');
      const holder = productGridContainer.querySelector('.hbc-applied-filters-holder');
      if (holder) {
        holder.appendChild(appliedContainer);
      } else {
        productGridContainer.insertBefore(appliedContainer, productGridContainer.firstChild);
      }
    }

    if (!appliedContainer) return;

    // Remove any stale native price or availability chips if present to avoid duplication
    appliedContainer.querySelectorAll('.hbc-applied-filters__chip:not(.hbc-applied-filters__chip--cradle-type):not(.hbc-applied-filters__chip--price):not(.hbc-applied-filters__chip--age)').forEach((chip) => {
      if (chip.href && (chip.href.includes('filter.v.price') || chip.href.includes('filter.v.availability') || chip.textContent.includes('Availability') || chip.textContent.includes('Under ') || chip.textContent.includes('Above '))) {
        chip.remove();
      }
    });

    const getClearBtn = () => appliedContainer.querySelector('.hbc-applied-filters__clear');

    // 1. Manage Cradle Type chips
    appliedContainer.querySelectorAll('.hbc-applied-filters__chip--cradle-type').forEach((chip) => {
      const tId = chip.dataset.typeId;
      if (!effectiveCradleTypes.includes(tId)) {
        chip.remove();
      }
    });

    effectiveCradleTypes.forEach((typeId) => {
      let cradleChip = appliedContainer.querySelector(`.hbc-applied-filters__chip--cradle-type[data-type-id="${typeId}"]`);
      const typeLabel = typeId === 'all' ? 'All Types' : (typeId.charAt(0).toUpperCase() + typeId.slice(1));
      if (!cradleChip) {
        cradleChip = document.createElement('a');
        cradleChip.className = 'hbc-applied-filters__chip hbc-applied-filters__chip--cradle-type';
        cradleChip.dataset.typeId = typeId;
        cradleChip.href = '#';
        cradleChip.setAttribute('role', 'button');
        cradleChip.setAttribute('aria-label', `Remove Cradle Type: ${typeLabel} filter`);
        cradleChip.innerHTML = `Cradle Type: ${typeLabel} <span class="svg-wrapper"><svg viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8m0-8L2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>`;
        const clearBtn = getClearBtn();
        if (clearBtn) {
          appliedContainer.insertBefore(cradleChip, clearBtn);
        } else {
          appliedContainer.appendChild(cradleChip);
        }
      }
    });

    // 2. Manage Price Range chips
    appliedContainer.querySelectorAll('.hbc-applied-filters__chip--price').forEach((chip) => {
      const rId = chip.dataset.rangeId;
      if (!selectedPriceRanges.includes(rId)) {
        chip.remove();
      }
    });

    selectedPriceRanges.forEach((rangeId) => {
      let priceChip = appliedContainer.querySelector(`.hbc-applied-filters__chip--price[data-range-id="${rangeId}"]`);
      const label = FacetFiltersForm.getPriceRangeLabel(rangeId);
      if (!priceChip) {
        priceChip = document.createElement('a');
        priceChip.className = 'hbc-applied-filters__chip hbc-applied-filters__chip--price';
        priceChip.dataset.rangeId = rangeId;
        priceChip.href = '#';
        priceChip.setAttribute('role', 'button');
        priceChip.setAttribute('aria-label', `Remove ${label} filter`);
        priceChip.innerHTML = `${label} <span class="svg-wrapper"><svg viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8m0-8L2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>`;
        const clearBtn = getClearBtn();
        if (clearBtn) {
          appliedContainer.insertBefore(priceChip, clearBtn);
        } else {
          appliedContainer.appendChild(priceChip);
        }
      }
    });

    // 3. Manage Age Group chips
    appliedContainer.querySelectorAll('.hbc-applied-filters__chip--age').forEach((chip) => {
      const aId = chip.dataset.ageId;
      if (!selectedAgeGroups.includes(aId)) {
        chip.remove();
      }
    });

    selectedAgeGroups.forEach((ageId) => {
      let ageChip = appliedContainer.querySelector(`.hbc-applied-filters__chip--age[data-age-id="${ageId}"]`);
      const label = FacetFiltersForm.getAgeGroupLabel(ageId);
      if (!ageChip) {
        ageChip = document.createElement('a');
        ageChip.className = 'hbc-applied-filters__chip hbc-applied-filters__chip--age';
        ageChip.dataset.ageId = ageId;
        ageChip.href = '#';
        ageChip.setAttribute('role', 'button');
        ageChip.setAttribute('aria-label', `Remove ${label} filter`);
        ageChip.innerHTML = `${label} <span class="svg-wrapper"><svg viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8m0-8L2 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>`;
        const clearBtn = getClearBtn();
        if (clearBtn) {
          appliedContainer.insertBefore(ageChip, clearBtn);
        } else {
          appliedContainer.appendChild(ageChip);
        }
      }
    });

    // Ensure "Remove all" exists if any chips exist
    const currentChips = appliedContainer.querySelectorAll('.hbc-applied-filters__chip');
    let clearLink = appliedContainer.querySelector('.hbc-applied-filters__clear');
    if (currentChips.length > 0) {
      if (!clearLink) {
        clearLink = document.createElement('a');
        clearLink.className = 'hbc-applied-filters__clear';
        clearLink.href = '#';
        clearLink.setAttribute('role', 'button');
        clearLink.textContent = 'Remove all';
        appliedContainer.appendChild(clearLink);
      }
    } else if (appliedContainer) {
      appliedContainer.remove();
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.originalPaginationHTML = '';
FacetFiltersForm.currentFilteredPage = 1;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

const initFacetState = () => {
  const initPagination = document.querySelector(
    '#ProductGridContainer .pagination-wrapper, .template-search__results .pagination-wrapper, .collection .pagination-wrapper'
  );
  if (initPagination && !initPagination.classList.contains('hbc-dynamic-pagination')) {
    FacetFiltersForm.originalPaginationHTML = initPagination.outerHTML;
  }
  FacetFiltersForm.syncCradleCardsFromURL();
  FacetFiltersForm.syncPriceCardsFromURL();
  FacetFiltersForm.syncAgeCardsFromURL();
  FacetFiltersForm.applyPriceAndCradleFilters();
};

// Initialize cradle type & price filters on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFacetState);
} else {
  initFacetState();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.cards = this.querySelectorAll('.hbc-price-card__checkbox');
    if (this.cards.length === 0) {
      this.querySelectorAll('input').forEach((element) => {
        element.addEventListener('change', this.onRangeChange.bind(this));
        element.addEventListener('keydown', this.onKeyDown.bind(this));
      });
      this.setMinAndMaxValues();
    }
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"])');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
